import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";
import "./rolePage.css";

//Components
import ItemCard from "../../Components/Item Components/itemCard";
import ItemCardHeader from "../../Components/Item Components/itemCardHeader";
import Title from "../../Components/Other Components/Title";
import SearchBar from "../../Components/Other Components/searchBar";
import SortComponent from "../../Components/Other Components/sortComponent";
import PageLoader from "../../Components/Other Components/pageLoader";
import ColorCircle from "../../Components/Tag Components/colorCircle";
import Button from "../../Components/Other Components/button";

//Functions and hooks
import { GetRoles, CheckForRole } from "../../BackendAccess/RoleHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { useTranslation } from "react-i18next";

export default function RolePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);
  const isMounted = useRef(true);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredItems, setSearchFilteredItems] = useState([]);

  const { t } = useTranslation();

  const lazyLoadItemLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  // Use a callback to prevent stale closures
  const fetchRoles = useCallback(async () => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const [rolesData, rolesError] = await fetchWithTimeout(GetRoles);

      if (!isMounted.current) return;

      if (rolesError) {
        rolesError.isPageError = true;
        setError(rolesError);
      } else {
        setRoles(rolesData);
      }
    } catch (error) {
      if (isMounted.current) {
        setError({
          err: t("server.Server error", { ns: "error" }),
          errCode: 500,
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchRoles();
    return () => {
      isMounted.current = false;
    };
  }, [fetchRoles]);

  useEffect(() => {
    if (isMounted.current) {
      fetchRoles();
    }
  }, [location.pathname, fetchRoles]);

  useEffect(() => {
    if (roles.length > 0) setFilteredItems(roles);
  }, [roles]);

  const handleEditClick = async (id) => {
    console.log("Checking for role");
    const [bItemExists, itemExistsError] = await CheckForRole(id);
    if (itemExistsError) {
      setError(itemExistsError);
      console.log("Role doesnt exist", itemExistsError);
      return;
    }
    if (bItemExists) {
      console.log("Role gooddd");

      navigate("/workspace/roles/roleEditPage", { state: { id } });
    } else {
      console.log("Role errorrr", bItemExists, id);

      setError({ err: t("Role not found.", { ns: "roles" }), errCode: 404 });
    }
  };

  const handleCreateClick = () => {
    navigate("/workspace/roles/roleCreationPage");
  };

  //***************************SEARCHING AND SORTING******************************

  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter items based on search term
  useEffect(() => {
    const newSearchFilteredItems = filteredItems.filter((item) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        item.Name.toLowerCase().includes(lowerCaseTerm) ||
        item.RoleID.toString().toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredItems(newSearchFilteredItems);
  }, [searchTerm, filteredItems]);

  const nameSortFunction = (direction) => {
    const rolesSorted = [...filteredItems].sort((a, b) =>
      a.Name.toUpperCase() > b.Name.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setRoles(rolesSorted);
  };

  const idSortFunction = (direction) => {
    const rolesSorted = [...filteredItems].sort((a, b) =>
      a.RoleID > b.RoleID ? direction * 1 : -1 * direction
    );
    setRoles(rolesSorted);
  };

  const priorityLevelSortFunction = (direction) => {
    const rolesSorted = [...filteredItems].sort((a, b) =>
      a.PriorityLevel > b.PriorityLevel ? direction * 1 : -1 * direction
    );
    setRoles(rolesSorted);
  };

  //***************************SEARCHING AND SORTING END******************************

  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredItems,
    lazyLoadItemLoadCount,
    lazyLoadPixelThreshold
  );
  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  ////////////////////////////////////////Creating cards///////////////////////////////////////////
  const propertiesToShow = {
    ID: { Show: true, Width: 1 },
    Name: { Show: true, Width: 3 },
    PriorityLevel: { Show: true, Width: 3 },
    Color: { Show: true, Width: 2 },
  };

  const columnWidths = Object.values(propertiesToShow).map(
    (item) => item.Width
  );

  const createHeaderCardProps = () => {
    const cardPropArray = [
      { Value: "ID", columnWidth: columnWidths[0] },
      { Value: "Name", columnWidth: columnWidths[1] },
      ...(isNarrow
        ? []
        : [{ Value: "PriorityLevel", columnWidth: columnWidths[1] }]),
      { Value: "Color", columnWidth: columnWidths[2] },
    ];

    return cardPropArray.filter((item) => propertiesToShow[item.Value].Show);
  };

  const createCardProps = (item) => {
    const cardPropArray = [
      { Name: "ID", Value: item.RoleID, columnWidth: columnWidths[0] },
      { Name: "Name", Value: item.Name, columnWidth: columnWidths[1] },
      ...(isNarrow
        ? []
        : [
            {
              Name: "PriorityLevel",
              Value: item.PriorityLevel,
              columnWidth: columnWidths[1],
            },
          ]),
      {
        Name: "Color",
        Value: (
          <ColorCircle
            clickable={false}
            color={item.Color}
            onClick={() => {}}
          />
        ),
        columnWidth: columnWidths[2],
      },
    ];

    return cardPropArray.filter((item) => propertiesToShow[item.Name].Show);
  };

  ////////////////////////////////////////Creating cards end///////////////////////////////////////////

  const tutorialSteps = [
    {
      target: null,
      title: t("Your roles", { ns: "tutorial" }),
      message: t(
        "This is your Roles page - roles define what employees are allowed to see and do in the app. Each role is a bundle of permissions you can assign to any number of employees.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".search-bar-container",
      title: t("Search", { ns: "tutorial" }),
      message: t("Search for a role by name or ID.", { ns: "tutorial" }),
    },
    {
      target: ".sortAndFilterBar",
      title: t("Sort", { ns: "tutorial" }),
      message: t(
        "Sort roles by name, ID, or priority level.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".CardHeader .btn-positive",
      title: t("Create a role", { ns: "tutorial" }),
      message: t(
        "Add a new role here, then choose exactly which permissions it grants.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-card-container .Card:first-child",
      title: t("Role rows", { ns: "tutorial" }),
      message: t(
        "Each row is one role, with its ID, name, priority level, and color. Click Edit to change its permissions.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="roles" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">Roles</Title>
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search roles...", { ns: "roles" })}
            />
          </div>
          <div className="sortAndFilterBar">
            <SortComponent
              sortOpts={["Name", "ID", "Priority Level"]}
              sortOptFunctions={[
                nameSortFunction,
                idSortFunction,
                priorityLevelSortFunction,
              ]}
            />
          </div>
          <div className="item-card-and-header-container">
            <ItemCardHeader
              propertiesToShow={createHeaderCardProps()}
              bBtnVisible={true}
              bBtnSectionVisible={true}
              btnSectionWidth={1}
              onClick={handleCreateClick}
              btnText={"Create"}
            />
            <div className="item-card-container" ref={itemContainerRef}>
              {loadedItems.map((item) => (
                <ItemCard
                  key={item.RoleID}
                  ItemID={item.RoleID}
                  propertiesToShow={createCardProps(item)}
                  bBtnVisible={true}
                  bBtnSectionVisible={true}
                  btnSectionWidth={1}
                  onClick={() => {
                    handleEditClick(item.RoleID);
                  }}
                  btnText={t("Edit")}
                />
              ))}
            </div>
          </div>
          {isNarrow ? (
            <div className="item-view-narrow-btn-container">
              <Button
                btnStyle={"btn-positive"}
                icon="create"
                onClick={handleCreateClick}
              >
                {t("Create")}
              </Button>
            </div>
          ) : null}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
